import type { ThemeConfig } from "antd";

/**
 * Theme aligned with david-andersen.no — warm-luxury, minimalist.
 *
 * Principles:
 *   - Rework Display for UI/display copy + Test Signifier for DA wordmark/details
 *   - Warm neutrals: cream #FAF9F5, sand #EFE6DA
 *   - Near-black for text + primary actions; gold lives in shadows only
 *   - Sharp corners, generous whitespace, light mode only
 *   - Avoid rounded "friendly" UI, gradient blobs, glassmorphism
 *
 * The gold-accent shadow (rgba 175, 148, 111, 0.2) is applied per-component
 * where it adds value — we don't let antd auto-glow buttons in gold.
 */
export const gullTheme: ThemeConfig = {
  token: {
    colorPrimary: "#1F1F1F",
    colorLink: "#1F1F1F",
    colorLinkHover: "#4A3D1F",
    colorText: "#1F1F1F",
    colorTextSecondary: "#6B6358",
    colorTextTertiary: "#8A8378",
    colorBgLayout: "#FAF9F5",
    colorBgContainer: "#FFFFFF",
    colorBgElevated: "#FFFFFF",
    colorBorder: "#EFE6DA",
    colorBorderSecondary: "#F5EFE3",
    colorFillSecondary: "#F5EFE3",
    colorFillTertiary: "#FAF9F5",
    colorFillQuaternary: "#FCFBF7",
    colorSuccess: "#3F7A4B",
    colorWarning: "#B4842E",
    colorError: "#9B2E2E",
    colorInfo: "#1F1F1F",
    borderRadius: 0,
    borderRadiusLG: 0,
    borderRadiusSM: 0,
    borderRadiusXS: 0,
    controlHeight: 40,
    fontSize: 15,
    fontSizeHeading1: 36,
    fontSizeHeading2: 28,
    fontSizeHeading3: 22,
    fontFamily: '"rework-display", "Avenir Next", "Helvetica Neue", Helvetica, Arial, sans-serif',
    lineHeight: 1.6,
  },
  components: {
    Button: {
      // Square corners, slightly taller hit area, no shadow.
      controlHeight: 44,
      controlHeightLG: 52,
      paddingInline: 22,
      paddingInlineLG: 28,
      fontWeight: 500,
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
    },
    Card: {
      borderRadiusLG: 0,
      // Hairline border on cream, no shadow by default — relies on the
      // background contrast between #FFFFFF cards and #FAF9F5 layout to
      // separate sections without competing for attention.
      headerBg: "transparent",
      paddingLG: 28,
    },
    Input: {
      controlHeight: 44,
      paddingInline: 14,
    },
    InputNumber: {
      controlHeight: 44,
    },
    Select: {
      controlHeight: 44,
    },
    DatePicker: {
      controlHeight: 44,
    },
    Tag: {
      borderRadiusSM: 0,
      defaultBg: "#F5EFE3",
      defaultColor: "#1F1F1F",
    },
    Tabs: {
      itemSelectedColor: "#1F1F1F",
      itemHoverColor: "#4A3D1F",
      inkBarColor: "#1F1F1F",
      titleFontSize: 15,
    },
    Table: {
      headerBg: "#FAF9F5",
      headerColor: "#6B6358",
      rowHoverBg: "#FCFBF7",
      borderColor: "#F5EFE3",
    },
    Alert: {
      borderRadiusLG: 0,
      colorInfoBg: "#FCFBF7",
      colorInfoBorder: "#EFE6DA",
    },
    Modal: {
      borderRadiusLG: 0,
      contentBg: "#FFFFFF",
      headerBg: "#FFFFFF",
    },
    Form: {
      labelColor: "#4A4239",
      labelFontSize: 13,
    },
    Steps: {
      iconSize: 28,
      titleLineHeight: 1.4,
    },
    Typography: {
      titleMarginTop: 0,
      titleMarginBottom: "0.6em",
    },
    Divider: {
      colorSplit: "#EFE6DA",
    },
    Menu: {
      itemBorderRadius: 0,
      itemSelectedBg: "#F5EFE3",
      itemSelectedColor: "#1F1F1F",
      itemHoverBg: "#FAF9F5",
    },
  },
};
